import keys from '@debbie/common/dist/cache/keys.js';
import { Timing } from '@debbie/common/dist/economy/billing-appendix-setups/index.js';
import { billingSelectorToString, } from '@debbie/common/dist/economy/billing/index.js';
import { getBillingSums } from '@debbie/common/dist/economy/billing/organize.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import * as billingAPI from '@debbie/cortex/dist/api/billing.js';
import { billingsEntity, useOngoingWork, useOngoingWorkBilling, } from '@debbie/cortex/dist/billings/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import q from '@debbie/cortex/dist/query.js';
import moment from 'moment';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../theme.js';
import { errorNotification } from '../../utils/errors.js';
import AddBillingVoucher from '../AddBillingVoucher.js';
import BillingPreview from '../BillingPreview/BillingPreview.js';
import Button from '../Button.js';
import { Plus } from '../Icons/index.js';
import { Badge, Modal, PlaceholderLoader } from '../index.js';
import PowerTable from '../PowerTable.js';
import { Tab, TabList, TabPanel, Tabs } from '../Tabs.js';
import { powerTableValues } from './billing-table-utils.js';
const genKey = (ongoingWork) => {
    if (ongoingWork.isAdjustment) {
        return 'adjustment';
    }
    if (ongoingWork.billingPrevented) {
        return 'billingPrevented';
    }
    const billAfterExceeded = ongoingWork.billAfter && moment(ongoingWork.billAfter).isBefore(moment());
    const caseEnded = ongoingWork.timingType === Timing.OnCaseEnd ? ongoingWork.caseEnded : false;
    const isASAP = ongoingWork.timingType === Timing.ASAP;
    const when = isASAP || billAfterExceeded || caseEnded ? 'now' : ongoingWork.timingType;
    return `${when}-${when === 'now' ? 'null' : ongoingWork.billAfter}`;
};
const TimingOrdering = [
    Timing.ASAP,
    Timing.Manual,
    Timing.Weekly,
    Timing.Monthly,
    Timing.Quarterly,
    Timing.OnCaseEnd,
];
const BillingCollections = (props) => {
    const lang = useSelector((s) => s.lang);
    const site = useSelector((s) => s.site);
    const ongoingWorkData = useOngoingWork({
        creditorId: props.creditorId,
        caseId: props.caseId,
    });
    const [groups, setGroups] = React.useState(null);
    const [selected, setSelected] = React.useState([]);
    const [previewOngoingWork, setPreviewOngoingWork] = React.useState(null);
    const [addBillingVoucherOpen, setAddBillingVoucherOpen] = React.useState(false);
    const ongoingWork = h.hasData(ongoingWorkData) ? ongoingWorkData.data : null;
    const billSelectedMutation = useMutation(async () => {
        if (!ongoingWork) {
            return;
        }
        await billingAPI.createBillingCollection({
            newBillings: ongoingWork
                .filter((ow) => selected.includes(billingSelectorToString(ow)))
                .map((_ongoingWork) => ({
                billingAppendixId: _ongoingWork.billingAppendixId,
                creditorId: _ongoingWork.creditorId,
                currency: _ongoingWork.currency,
                isAdjustment: _ongoingWork.isAdjustment,
                billAfter: _ongoingWork.billAfter,
                caseId: props.caseId || undefined,
                caseEnded: _ongoingWork.caseEnded,
                billingPrevented: _ongoingWork.billingPrevented,
            })),
        });
        q.invalidate([...keys.write.create(billingsEntity)]);
        setSelected([]);
    }, {
        onError: (err) => {
            errorNotification(lang, err, 'create', lang.errorEntities.billing);
        },
    });
    useEffect(() => {
        if (ongoingWork) {
            const ASAP_group = {
                billAfter: null,
                isAdjustment: false,
                timingType: Timing.ASAP,
                caseEnded: null,
                billingPrevented: false,
            };
            const groupsMap = {
                [genKey(ASAP_group)]: {
                    ...ASAP_group,
                    ongoingWork: [],
                    key: genKey(ASAP_group),
                },
            };
            for (let i = 0; i < ongoingWork.length; i++) {
                const element = ongoingWork[i];
                // Check if can be added to existing
                if (groupsMap[genKey(element)]) {
                    groupsMap[genKey(element)].ongoingWork =
                        groupsMap[genKey(element)].ongoingWork.concat(element);
                }
                else {
                    // Create new type
                    groupsMap[genKey(element)] = {
                        key: genKey(element),
                        timingType: element.timingType,
                        billAfter: element.billAfter,
                        isAdjustment: element.isAdjustment,
                        billingPrevented: element.billingPrevented,
                        ongoingWork: [element],
                    };
                }
            }
            setGroups(Object.values(groupsMap).sort((a, b) => TimingOrdering.indexOf(a.timingType) -
                TimingOrdering.indexOf(b.timingType)));
        }
    }, [ongoingWork]);
    return (React.createElement(StyledBillingCollections, null,
        groups ? (React.createElement(Tabs, { defaultIndex: 0 },
            React.createElement(TabList, null,
                groups.map((_type) => {
                    if (_type.isAdjustment) {
                        return (React.createElement(Tab, { key: _type.key },
                            lang.billing.adjustments,
                            ' ',
                            React.createElement(Badge, { theme: THEME.COLOR_ACCENT }, _type.ongoingWork.length)));
                    }
                    else if (_type.billingPrevented) {
                        return (React.createElement(Tab, { key: _type.key },
                            lang.billing.billingPrevented,
                            ' ',
                            React.createElement(Badge, { theme: THEME.COLOR_ACCENT }, _type.ongoingWork.length)));
                    }
                    else if (_type.timingType === Timing.ASAP ||
                        (_type.billAfter && moment(_type.billAfter).isBefore(moment()))) {
                        return (React.createElement(Tab, { key: _type.key, style: {
                                opacity: _type.ongoingWork.length === 0 ? 0.7 : 1,
                            } },
                            lang.billing.readyForBilling,
                            ' ',
                            React.createElement(Badge, { theme: _type.ongoingWork.length === 0
                                    ? THEME.NEUTRAL
                                    : THEME.COLOR_ACCENT }, _type.ongoingWork.length)));
                    }
                    else if (_type.timingType === Timing.Manual) {
                        return (React.createElement(Tab, { key: _type.key },
                            lang.billingAppendixSetupTimings[Timing.Manual],
                            ' ',
                            React.createElement(Badge, { theme: THEME.COLOR_ACCENT }, _type.ongoingWork.length)));
                    }
                    else if (_type.billAfter &&
                        moment(_type.billAfter).isAfter(moment())) {
                        return (React.createElement(Tab, { key: _type.key, style: { opacity: 0.7 } },
                            lang.billingAppendixSetupTimings[_type.timingType],
                            ' ',
                            lang.misc.after,
                            ' ',
                            moment(_type.billAfter).format(THEME.DATE_FORMAT),
                            ' ',
                            React.createElement(Badge, { theme: THEME.NEUTRAL }, _type.ongoingWork.length)));
                    }
                    else if (_type.timingType === Timing.OnCaseEnd) {
                        return (React.createElement(Tab, { key: _type.key, style: { opacity: 0.7 } },
                            lang.billing.awaitingCaseEnd,
                            ' ',
                            React.createElement(Badge, { theme: THEME.NEUTRAL }, _type.ongoingWork.length)));
                    }
                    else {
                        return (React.createElement(Tab, { key: _type.key, style: { opacity: 0.7 } },
                            lang.billing.appendixMissing,
                            ' ',
                            React.createElement(Badge, { theme: THEME.NEUTRAL }, _type.ongoingWork.length)));
                    }
                }),
                ongoingWork && (React.createElement(Button, { size: "small", style: { float: 'right' }, disabled: selected.length === 0, theme: selected.length === 0 ? 'secondary' : undefined, loading: billSelectedMutation.isLoading, onClick: () => billSelectedMutation.mutate() },
                    lang.billing.billSelected,
                    ' ',
                    React.createElement(Badge, { theme: selected.length > 0 ? THEME.COLOR_SECONDARY : THEME.NEUTRAL, style: {
                            color: selected.length > 0
                                ? THEME.COLOR_ACCENT
                                : THEME.COLOR_SECONDARY,
                        } }, selected.length))),
                React.createElement(Button, { size: "small", style: { float: 'right', marginRight: '5px' }, onClick: () => setAddBillingVoucherOpen(!addBillingVoucherOpen) },
                    lang.billing.addBillingVoucher,
                    " ",
                    React.createElement(Plus, null))),
            groups.map((_type) => {
                return (React.createElement(TabPanel, { key: _type.key }, _type.ongoingWork.length === 0 ? (React.createElement("div", null,
                    React.createElement(NoSelected, null))) : (React.createElement("div", { style: { fontSize: '0.8em' } },
                    React.createElement(PowerTable, { data: _type.ongoingWork.map((ongoingWork) => ({
                            creditorName: ongoingWork.creditorName,
                            creditorBankAccount: ongoingWork.creditorBankAccount,
                            creditorId: ongoingWork.creditorId,
                            creditorReferenceId: ongoingWork.creditorReferenceId,
                            billingEmails: ongoingWork.billingEmails,
                            billingAppendixId: ongoingWork.billingAppendixId,
                            handle: ongoingWork.handle,
                            timingType: ongoingWork.timingType,
                            isAdjustment: ongoingWork.isAdjustment,
                            billAfter: ongoingWork.billAfter,
                            caseEnded: ongoingWork.caseEnded,
                            caseSequentialIds: ongoingWork.caseSeqIds,
                            revenue: ongoingWork.revenue +
                                ongoingWork.revenueCollectionCommission,
                            vat: ongoingWork.vat,
                            payout: ongoingWork.payout,
                            disbursements: ongoingWork.disbursements,
                            overpayments: ongoingWork.overpayments,
                            balance: getBillingSums(ongoingWork).balance,
                            currency: ongoingWork.currency,
                            billingPrevented: ongoingWork.billingPrevented,
                        })), genKey: billingSelectorToString, 
                        // @ts-ignore
                        defaultSortKey: "revenue", defaultSortDirection: "DESC", searchable: true, toSearchStr: (ow) => JSON.stringify(ow), selectable: true, selectKey: billingSelectorToString, onSelect: setSelected, selected: selected, onClick: (ongoingWork) => {
                            setPreviewOngoingWork({
                                creditorId: ongoingWork.creditorId,
                                currency: ongoingWork.currency,
                                isAdjustment: ongoingWork.isAdjustment,
                                billingAppendixId: ongoingWork.billingAppendixId,
                                billAfter: ongoingWork.billAfter,
                                caseEnded: ongoingWork.caseEnded,
                                billingPrevented: ongoingWork.billingPrevented,
                            });
                        }, pagination: {
                            type: 'pages',
                            pageSize: 50,
                        }, 
                        // @ts-ignore
                        values: powerTableValues({
                            lang,
                            // @ts-ignore
                            site,
                            handleCategories: _type.ongoingWork.reduce((acc, ongoingWork) => {
                                if (!acc[ongoingWork.handle]) {
                                    acc[ongoingWork.handle] = {
                                        label: ongoingWork.handle,
                                        type: ongoingWork.handle,
                                    };
                                }
                                return acc;
                            }, {}),
                        }) })))));
            }))) : (React.createElement(PlaceholderLoader, null)),
        previewOngoingWork && (React.createElement(Modal, { onToggle: () => setPreviewOngoingWork(null), maxWidth: "1600px", header: lang.billing.appendix },
            React.createElement(BillingPreviewOngoingWork, { billingSelector: previewOngoingWork, highlightCaseId: props.highlightCaseId, closeModal: () => setPreviewOngoingWork(null) }))),
        addBillingVoucherOpen && (React.createElement(Modal, { onToggle: () => setAddBillingVoucherOpen(!addBillingVoucherOpen), maxWidth: "600px", header: lang.billing.addBillingVoucher },
            React.createElement(AddBillingVoucher, { caseId: props.caseId, onAdd: () => setAddBillingVoucherOpen(false) })))));
};
export default BillingCollections;
const StyledBillingCollections = styled.div ``;
const BillingPreviewOngoingWork = (props) => {
    const billingLoadable = useOngoingWorkBilling(props.billingSelector);
    const billing = h.hasData(billingLoadable) ? billingLoadable.data : null;
    return billing ? (React.createElement(BillingPreview, { billing: billing, billingSelector: props.billingSelector, closeModal: props.closeModal, highlightCaseId: props.highlightCaseId })) : (React.createElement(PlaceholderLoader, null));
};
const NoSelected = () => (React.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", "data-name": "Layer 1", width: "1008.92039", height: "607.44997", viewBox: "0 0 1008.92039 607.44997", style: {
        width: '150px',
        height: '150px',
        margin: '50px auto',
        display: 'block',
    } },
    React.createElement("path", { d: "M1104.46,620.305a15.34,15.34,0,0,1-15.25952,15.42H417.8a15.34,15.34,0,0,1-15.26-15.41955V161.695a15.34,15.34,0,0,1,15.25958-15.42H1089.2a15.34,15.34,0,0,1,15.26,15.41957h0V620.305Z", transform: "translate(-95.53981 -146.27502)", fill: "#e6e6e6" }),
    React.createElement("path", { d: "M1096.54006,612.795a14.91,14.91,0,0,1-14.91,14.91H425.83a14.91,14.91,0,0,1-14.91-14.91V169.415a14.91,14.91,0,0,1,14.91-14.91h655.83a14.91,14.91,0,0,1,14.88,14.91Z", transform: "translate(-95.53981 -146.27502)", fill: "#fff" }),
    React.createElement("path", { id: "a90d98f2-fe2a-41dc-85de-0aa221261a3e-339", "data-name": "b9c54412-061c-4155-b289-fb4c23e4a64e", d: "M445.97,173.755h616.22a8.85,8.85,0,0,1,8.85,8.85v415.28a8.85,8.85,0,0,1-8.85,8.85H445.97a8.85,8.85,0,0,1-8.85-8.85h0V182.605a8.85,8.85,0,0,1,8.85-8.85Z", transform: "translate(-95.53981 -146.27502)", fill: "#e6e6e6" }),
    React.createElement("path", { d: "M453.77,183.715h599.95a8.85,8.85,0,0,1,8.85,8.85v393.12a8.85,8.85,0,0,1-8.85,8.85H453.77a8.85,8.85,0,0,1-8.85-8.85h0V192.565a8.85,8.85,0,0,1,8.85-8.85Z", transform: "translate(-95.53981 -146.27502)", fill: "#fff" }),
    React.createElement("path", { d: "M949.46,675.305a15.34,15.34,0,0,1-15.25958,15.42H262.8a15.34,15.34,0,0,1-15.26-15.41961h0V216.695a15.34,15.34,0,0,1,15.25957-15.42H934.2a15.34,15.34,0,0,1,15.26,15.41957h0V675.305Z", transform: "translate(-95.53981 -146.27502)", fill: "#e6e6e6" }),
    React.createElement("path", { d: "M941.54,667.795a14.91,14.91,0,0,1-14.91,14.91H270.83a14.91,14.91,0,0,1-14.91-14.91V224.415a14.91,14.91,0,0,1,14.91-14.91H926.66a14.91,14.91,0,0,1,14.88,14.91Z", transform: "translate(-95.53981 -146.27502)", fill: "#fff" }),
    React.createElement("path", { id: "b54624cf-b287-45f0-8f58-93aafc1fcbba-340", "data-name": "a7242049-80b4-49e1-bd08-67354734c824", d: "M290.97,228.755H907.19a8.85,8.85,0,0,1,8.85,8.85v415.28a8.85,8.85,0,0,1-8.85,8.85H290.97a8.85,8.85,0,0,1-8.85-8.85h0V237.605a8.85,8.85,0,0,1,8.85-8.85h0Z", transform: "translate(-95.53981 -146.27502)", fill: "#e6e6e6" }),
    React.createElement("path", { d: "M298.77,238.715H898.72a8.85,8.85,0,0,1,8.85,8.85v393.12a8.85,8.85,0,0,1-8.85,8.85H298.77a8.85,8.85,0,0,1-8.85-8.85h0V247.565a8.85,8.85,0,0,1,8.85-8.85Z", transform: "translate(-95.53981 -146.27502)", fill: "#fff" }),
    React.createElement("path", { d: "M797.46,738.305a15.34,15.34,0,0,1-15.25958,15.42H110.8a15.34,15.34,0,0,1-15.26-15.41955V279.695a15.34,15.34,0,0,1,15.25957-15.42H782.2a15.34,15.34,0,0,1,15.26,15.41959h0V738.305Z", transform: "translate(-95.53981 -146.27502)", fill: "#e6e6e6" }),
    React.createElement("path", { d: "M789.54,730.795a14.91,14.91,0,0,1-14.91,14.91H118.83a14.91,14.91,0,0,1-14.91-14.91V287.415a14.91,14.91,0,0,1,14.91-14.91H774.66a14.91,14.91,0,0,1,14.88,14.91Z", transform: "translate(-95.53981 -146.27502)", fill: "#fff" }),
    React.createElement("path", { id: "a665a07e-790f-4a9c-be99-8c13ca89238c-341", "data-name": "b7a050eb-0cb2-44a8-b642-35d340850eff", d: "M138.97,291.755H755.19a8.85,8.85,0,0,1,8.85,8.85v415.28a8.85,8.85,0,0,1-8.85,8.85H138.97a8.85,8.85,0,0,1-8.85-8.85V300.605a8.85,8.85,0,0,1,8.85-8.85h0Z", transform: "translate(-95.53981 -146.27502)", fill: "#e6e6e6" }),
    React.createElement("path", { d: "M146.77,301.715H746.72a8.85,8.85,0,0,1,8.85,8.85v393.12a8.85,8.85,0,0,1-8.85,8.85H146.77a8.85,8.85,0,0,1-8.85-8.85V310.565A8.85,8.85,0,0,1,146.77,301.715Z", transform: "translate(-95.53981 -146.27502)", fill: "#fff" }),
    React.createElement("path", { d: "M590.14174,386.874v272a6.00477,6.00477,0,0,1-6,6h-285a6.00476,6.00476,0,0,1-6-6v-304a6.00475,6.00475,0,0,1,6-6h249Z", transform: "translate(-95.53981 -146.27502)", fill: "#776afc" }),
    React.createElement("path", { id: "b3cdc3b1-5d0a-4cee-b54c-a38ee088c012-342", "data-name": "Path 40", d: "M343.57782,415.072a3.755,3.755,0,0,0,0,7.50892h99.00454a3.755,3.755,0,1,0,.12329-7.50892q-.06165-.001-.12329,0Z", transform: "translate(-95.53981 -146.27502)", fill: "#fff" }),
    React.createElement("path", { id: "b79c0997-055d-434d-8704-cfc1ccad2281-343", "data-name": "Path 40", d: "M343.57782,450.291a3.755,3.755,0,0,0,0,7.50893H539.58236a3.755,3.755,0,0,0,.12329-7.50893q-.06165-.001-.12329,0Z", transform: "translate(-95.53981 -146.27502)", fill: "#fff" }),
    React.createElement("path", { id: "ed7d1f14-d1d4-40af-b9ad-7b5b5ece23df-344", "data-name": "Path 40", d: "M343.57782,485.51005a3.755,3.755,0,0,0,0,7.50893H539.58236a3.755,3.755,0,1,0,.12329-7.50893q-.06165-.001-.12329,0Z", transform: "translate(-95.53981 -146.27502)", fill: "#fff" }),
    React.createElement("path", { id: "ba3b747a-a6b2-4f39-a169-6ef4c21cd2ce-345", "data-name": "Path 40", d: "M343.57782,520.72906a3.755,3.755,0,0,0,0,7.50892H539.58236a3.755,3.755,0,1,0,.12329-7.50892q-.06165-.001-.12329,0Z", transform: "translate(-95.53981 -146.27502)", fill: "#fff" }),
    React.createElement("path", { id: "b6e4b743-2497-479e-800a-91c8b21b3df9-346", "data-name": "Path 40", d: "M343.57782,555.94807a3.755,3.755,0,0,0,0,7.50892H539.58236a3.755,3.755,0,1,0,.12329-7.50892q-.06165-.00105-.12329,0Z", transform: "translate(-95.53981 -146.27502)", fill: "#fff" }),
    React.createElement("path", { id: "f91f499d-e7c9-446d-84c9-bf78465468f6-347", "data-name": "Path 40", d: "M343.57782,591.16708a3.755,3.755,0,0,0,0,7.50892H539.58236a3.755,3.755,0,1,0,.12329-7.50892q-.06165-.00105-.12329,0Z", transform: "translate(-95.53981 -146.27502)", fill: "#fff" }),
    React.createElement("path", { d: "M590.14174,386.874h-38a4,4,0,0,1-4-4v-34h0Z", transform: "translate(-95.53981 -146.27502)", fill: "#776afc" }),
    React.createElement("path", { d: "M590.14174,386.874h-38a4,4,0,0,1-4-4v-34h0Z", transform: "translate(-95.53981 -146.27502)", opacity: "0.2" })));
//# sourceMappingURL=OngoingWork.js.map